import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import { Layout } from '../components/Layout';
import { USER_ROLE_LABEL, USER_STATUS_LABEL } from '../constants/labels';
import { getErrorMessage } from '../types/api-error';
import type { StoreMember, Store } from '../types/admin';

interface AddMembersResult {
  added: Array<{ userId: number; mobile: string }>;
  alreadyMembers: Array<{ userId: number; mobile: string }>;
  failed: Array<{ mobile: string; reason: string }>;
}

export function StoreMembersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const storeId = Number(id);
  const [store, setStore] = useState<Store | null>(null);
  const [members, setMembers] = useState<StoreMember[]>([]);
  const [mobilesInput, setMobilesInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<StoreMember | null>(null);
  const [error, setError] = useState('');
  const [addResult, setAddResult] = useState<AddMembersResult | null>(null);

  const fetchStore = async () => {
    if (!storeId) return;
    try {
      const { data } = await api.get<Store>(`/stores/${storeId}`);
      setStore(data);
    } catch {
      setError('加载门店信息失败');
    }
  };

  const fetchMembers = async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<StoreMember[]>(`/stores/${storeId}/members`);
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, '加载成员失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStore();
    fetchMembers();
  }, [storeId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeId) return;

    const mobiles = mobilesInput
      .split(/[\n,，]+/)
      .map((m) => m.trim())
      .filter(Boolean);

    if (mobiles.length === 0) {
      setError('请输入至少一个手机号');
      return;
    }

    setAdding(true);
    setError('');
    setAddResult(null);

    try {
      const { data } = await api.post<AddMembersResult>(`/stores/${storeId}/members`, { mobiles });
      setAddResult(data);
      setMobilesInput('');
      fetchMembers();
    } catch (err) {
      setError(getErrorMessage(err, '添加成员失败'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = (member: StoreMember) => {
    if (!storeId) return;
    setConfirmTarget(member);
  };

  const doRemove = async () => {
    if (!storeId || !confirmTarget) return;
    setRemovingUserId(confirmTarget.userId);
    setError('');

    try {
      await api.delete(`/stores/${storeId}/members/${confirmTarget.userId}`);
      setConfirmTarget(null);
      fetchMembers();
    } catch (err) {
      setError(getErrorMessage(err, '移除成员失败'));
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <Layout>
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
        >
          返回
        </button>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
          {store?.storeName ? `${store.storeName} - 成员管理` : '门店成员管理'}
        </h2>
      </div>

      <div className="bg-white rounded-xl shadow p-4 sm:p-6 mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">添加成员</h3>
        <form onSubmit={handleAdd} className="space-y-3">
          <textarea
            rows={4}
            placeholder="每行一个手机号，支持批量添加"
            value={mobilesInput}
            onChange={(e) => {
              setMobilesInput(e.target.value);
              setAddResult(null);
              setError('');
            }}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-base resize-none"
          />
          <button
            type="submit"
            disabled={adding}
            className="w-full sm:w-auto px-6 py-3 rounded-lg bg-indigo-600 text-white font-medium text-base hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-60"
          >
            {adding ? '添加中…' : '添加成员'}
          </button>
        </form>

        {error && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
        )}

        {addResult && (
          <div className="mt-3 space-y-2 text-sm">
            {addResult.added.length > 0 && (
              <div className="text-green-700 bg-green-50 rounded-md px-3 py-2">
                成功添加 {addResult.added.length} 人：{addResult.added.map((a) => a.mobile).join('、')}
              </div>
            )}
            {addResult.alreadyMembers.length > 0 && (
              <div className="text-amber-700 bg-amber-50 rounded-md px-3 py-2">
                已是成员 {addResult.alreadyMembers.length} 人：{addResult.alreadyMembers.map((a) => a.mobile).join('、')}
              </div>
            )}
            {addResult.failed.length > 0 && (
              <div className="text-red-700 bg-red-50 rounded-md px-3 py-2">
                失败 {addResult.failed.length} 人：
                <ul className="list-disc list-inside mt-1">
                  {addResult.failed.map((f) => (
                    <li key={f.mobile}>
                      {f.mobile}：{f.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">加载中…</div>
      ) : members.length === 0 ? (
        <div className="text-center py-10 text-gray-500">暂无成员</div>
      ) : (
        <>
          <div className="hidden sm:block bg-white rounded-xl shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">手机号</th>
                  <th className="px-4 py-3 text-left font-medium">昵称</th>
                  <th className="px-4 py-3 text-left font-medium">角色</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-left font-medium">加入时间</th>
                  <th className="px-4 py-3 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.map((m) => (
                  <tr key={m.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{m.mobile}</td>
                    <td className="px-4 py-3">{m.nickname || '-'}</td>
                    <td className="px-4 py-3">{USER_ROLE_LABEL[m.role]}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          m.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {USER_STATUS_LABEL[m.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {m.joinedAt ? new Date(m.joinedAt).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemove(m)}
                        disabled={removingUserId === m.userId}
                        className="inline-flex items-center px-3 py-1.5 rounded-md bg-red-50 text-red-700 text-xs font-medium hover:bg-red-100 active:bg-red-200 disabled:opacity-60"
                      >
                        {removingUserId === m.userId ? '移除中…' : '移除'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {members.map((m) => (
              <div key={m.userId} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">{m.mobile}</div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        m.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {USER_STATUS_LABEL[m.status]}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemove(m)}
                      disabled={removingUserId === m.userId}
                      className="inline-flex items-center px-2 py-1 rounded bg-red-50 text-red-700 text-xs font-medium hover:bg-red-100 active:bg-red-200 disabled:opacity-60"
                    >
                      {removingUserId === m.userId ? '…' : '移除'}
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>昵称</span>
                    <span className="text-gray-900">{m.nickname || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>角色</span>
                    <span className="text-gray-900">{USER_ROLE_LABEL[m.role]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>加入时间</span>
                    <span className="text-gray-900">
                      {m.joinedAt ? new Date(m.joinedAt).toLocaleString() : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {confirmTarget && (
        <ConfirmModal
          title="移除成员"
          message={`确定要移除成员 ${confirmTarget.nickname || confirmTarget.mobile} 吗？`}
          confirmText="移除"
          cancelText="取消"
          loading={removingUserId === confirmTarget.userId}
          onConfirm={doRemove}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </Layout>
  );
}
